import React from 'react';
import { LightbulbIcon } from 'lucide-react';
import { TopBanner } from '../components/TopBanner';
import { ParticipantTestimonials } from '../components/ParticipantTestimonials';
import { ExpertProfile } from '../components/ExpertProfile';
import { WebinarTimeline } from '../components/WebinarTimeline';
import { WebinarDetailsCard } from '../components/WebinarDetailsCard';
import { BonusSection } from '../components/BonusSection';
import { TrustpilotReviews } from '../components/TrustpilotReviews';
import { WhyDifferent } from '../components/WhyDifferent';
import { FAQSections } from '../components/FAQSections';
import { TransformationSummary } from '../components/TransformationSummary';
import { FinalCTA } from '../components/FinalCTA';
import { ChatTestimonials } from '../components/ChatTestimonials';
import { NewFooter } from '../components/NewFooter';
import { StickyBar } from '../components/StickyBar';
import { RegistrationProvider, useRegistration } from '../components/RegistrationContext';
const Adv2bContent = () => {
  const {
    openPopup
  } = useRegistration();
  return <>
      <div className="advertorial-content max-w-3xl mx-auto px-4 font-sans text-gray-800 pb-28" data-id="element-561">
        <TopBanner data-id="element-562" />

        {/* 1. HOOFDTITEL (Hero sectie aangepast) */}
        <section className="mb-10 text-center" data-id="element-563">
          <h1 className="text-[1.6rem] md:text-3xl font-bold text-[rgb(30,64,175)] mb-6 text-center leading-snug" data-id="element-564">
            Waarom jouw hond niet luistert - en waarom jij daar zelf de oorzaak
            van bent
          </h1>
          <p className="text-blue-600 font-semibold mb-8 text-center" data-id="element-565">
            Modern onderzoek naar honden laat zien dat we op een andere manier
            met hen om moeten gaan.
          </p>
          <div className="w-full mb-2" data-id="element-566">
            <img src="https://cdn.magicpatterns.com/uploads/t8jaMNfJ3z6DxQ27njofF8/Scherm_afbeelding_2025-08-21_om_23.44.08-min_(1).png" alt="Labrador aan de lijn op straat" className="w-full h-auto rounded-lg" data-id="element-567" />
          </div>
          <p className="text-xs text-gray-400 italic mb-6" data-id="element-568">
            Soms kan anderhalf uur alles veranderen in de relatie met je hond
          </p>
          <div className="w-full h-[1px] bg-gray-200" data-id="element-569"></div>
        </section>

        {/* 2. EERSTE TEKSTBLOK (Vervangt LisaStory) */}
        <section className="mb-12" data-id="element-570">
          <p className="mb-4 text-gray-800 leading-relaxed" data-id="element-571">
            Je hond springt op als er bezoek komt. Of hij blaft aan één stuk
            door. Of hij trekt zo hard aan de lijn dat wandelen geen plezier
            meer is. Je hebt het al tientallen keren gecorrigeerd. Je bent
            strenger geworden. Je hebt je stem verheven. Misschien heb je hem
            zelfs bestraft.
          </p>
          <p className="mb-4 text-gray-800 leading-relaxed" data-id="element-572">
            En toch gebeurt het steeds weer.
          </p>
          <p className="mb-8 text-gray-800 leading-relaxed" data-id="element-573">
            Wat de meeste baasjes op dat moment denken: mijn hond wil gewoon
            niet luisteren. Maar volgens gediplomeerd hondengedragsconsultant
            Amee de Bruijn is wat er in werkelijkheid aan de hand is, iets heel
            anders.
          </p>
          <div className="w-full mb-6" data-id="element-574">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3JyaDPTDobRjD5WRihmtQh/Scherm%C2%ADafbeelding_2025-08-15_om_15.32.12.png" alt="Innig moment tussen een man en zijn golden retriever die elkaar aandachtig aankijken" className="w-full h-auto rounded-lg" data-id="element-575" />
            <p className="text-xs text-gray-500 mt-2 text-center italic" data-id="element-576">
              Het moment waarop je eindelijk begrijpt wat je hond al die tijd
              probeerde te zeggen
            </p>
          </div>
        </section>

        {/* 3. TWEEDE TEKSTBLOK (Vervangt BreakthroughMoment) */}
        <section className="mb-12" data-id="element-577">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-578">
            Je praat heel de dag met lichamelijke signalen
          </h2>
          <p className="mb-4 text-gray-800 leading-relaxed" data-id="element-579">
            "Honden communiceren primair via lichaamstaal," legt Amee uit.
            "Blaffen en piepen zijn eigenlijk al de laatste uitingen - het punt
            waarop een hond niet meer weet hoe hij het anders moet zeggen."
          </p>
          <p className="mb-4 text-gray-800 leading-relaxed" data-id="element-580">
            De echte communicatie zit volgens haar in veel subtielere signalen.
            Een hond die gaapte toen je hem aanraakte. Een hond die zijn gewicht
            verplaatste toen je dichter bij hem kwam. Een hond die even wegkeek
            op het moment dat de spanning opliep. Geen toevallige bewegingen,
            zegt Amee - boodschappen.
          </p>
          <p className="mb-4 text-gray-800 leading-relaxed" data-id="element-581">
            Maar de meeste baasjes weten niet wat ze betekenen. "Niet omdat ze
            het niet willen weten," benadrukt ze, "maar omdat niemand het ze
            ooit heeft geleerd. Je wordt niet geboren met kennis van hondentaal.
            En in de meeste traditionele hondentrainingen staat het ook niet
            centraal."
          </p>
          <p className="mb-4 text-gray-800 leading-relaxed" data-id="element-582">
            Het gevolg is dat baasjes de hele aanloop missen. Ze zien niet wat
            er opbouwt. Ze zien alleen het eindpunt - het blaffen, het springen,
            het uitvallen - en dát is het moment waarop ze ingrijpen. Met een
            correctie. Met straf.
          </p>
          <p className="mb-6 text-gray-800 leading-relaxed font-semibold" data-id="element-583">
            "Maar tegen de tijd dat een hond zover is," zegt Amee, "heeft hij al
            lang geprobeerd iets te zeggen. En niemand heeft geluisterd."
          </p>
        </section>

        {/* 4. DERDE TEKSTBLOK */}
        <section className="mb-12" data-id="element-584">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-585">
            Je stem zegt: Stop daarmee. Maar je lichaam zegt: ik keur dit goed.
          </h2>
          <p className="mb-4 text-gray-800 leading-relaxed" data-id="element-586">
            Voor veel baasjes is dit het moment waarop alles op zijn plaats
            valt. Want het probleem is niet alleen dat baasjes hondentaal niet
            begrijpen - het probleem is ook dat baasjes zelf voortdurend
            signalen afgeven waar ze zich niet bewust van zijn.
          </p>
          <p className="mb-4 text-gray-800 leading-relaxed" data-id="element-587">
            Amee legt tijdens haar webinar{' '}
            <a href="/webinar-mc-adv2" onClick={e => {
            e.preventDefault();
            openPopup();
          }} className="text-blue-700 underline font-semibold cursor-pointer hover:text-blue-900" data-id="element-588">
              (klik hier om beschikbaarheid te zien)
            </a>{' '}
            een veelvoorkomende situatie uit: je hond springt op. Jij wordt
            boos. Je verheft je stem, je zegt streng "nee" of "af". Maar
            tegelijkertijd zeg je met je lichaam dat je zijn gedrag goedkeurt.
          </p>
          <p className="mb-4 text-gray-800 leading-relaxed italic font-semibold" data-id="element-589">
            "Jouw stem zegt: stop daarmee. Jouw lichaam zegt: ik keur dit goed."
          </p>
          <p className="mb-4 text-gray-800 leading-relaxed" data-id="element-590">
            En honden kijken meer naar lichaamstaal dan naar stemgeluid. "Dus
            wat denk je welk signaal er aankomt?" vraagt Amee.
          </p>
          <p className="mb-6 text-gray-800 leading-relaxed" data-id="element-591">
            Dit is slechts één voorbeeld. Amee beschrijft tientallen situaties
            per dag waarin hetzelfde gebeurt - waarin de bedoeling van de
            eigenaar en zijn lichaamstaal elkaar tegenspreken. En elke keer
            raakt de hond een stukje meer verward.
          </p>
        </section>

        {/* 5. VIERDE TEKSTBLOK */}
        <section className="mb-12" data-id="element-592">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-593">
            De oplossing is eenvoudiger dan je denkt
          </h2>
          <p className="mb-4 text-gray-800 leading-relaxed" data-id="element-594">
            "Hondentaal is geen mysterie," stelt Amee. "Het is een consistent
            systeem van signalen dat te leren is."
          </p>
          <p className="mb-4 text-gray-800 leading-relaxed" data-id="element-595">
            En zodra je het kent, gebeuren er volgens haar twee dingen tegelijk.
            Je begrijpt eindelijk wat je hond al die tijd probeerde te
            vertellen. En je leert hoe je zelf bewuster communiceert - met je
            lichaam, niet alleen met je stem.
          </p>
          <p className="mb-4 text-gray-800 leading-relaxed italic font-semibold" data-id="element-596">
            "Dat is het moment waarop het gedrag verandert. Niet omdat de hond
            anders wordt, maar omdat de communicatie eindelijk klopt."
          </p>
          <p className="mb-6 text-gray-800 leading-relaxed" data-id="element-597">
            De verwarring verdwijnt. En daarmee verdwijnt ook het gedrag dat
            zoveel frustratie veroorzaakte.
          </p>
        </section>

        {/* 6. SECTIE MET HET GEKLEURDE KADER */}
        <section className="mb-12" data-id="element-598">
          <div className="bg-blue-50 border-l-4 border-blue-700 rounded-r-lg p-8" data-id="element-599">
            <div className="flex items-center gap-3 mb-6" data-id="element-600">
              <LightbulbIcon className="w-6 h-6 text-blue-700" data-id="element-601" />
              <h2 className="text-2xl font-bold text-blue-900" data-id="element-602">
                Leer hondentaal in 90 minuten - gratis
              </h2>
            </div>

            <p className="mb-4 text-gray-800 leading-relaxed text-lg" data-id="element-603">
              Amee de Bruijn deelt deze kennis in{' '}
              <a href="/webinar-mc-adv2" onClick={e => {
              e.preventDefault();
              openPopup();
            }} className="text-blue-700 underline font-semibold cursor-pointer hover:text-blue-900" data-id="element-604">
                een gratis webinar van 90 minuten
              </a>{' '}
              voor hondeneigenaren die vastlopen met hun hond. Je leert welke
              signalen je hond gebruikt en wat ze betekenen. Je leert hoe je die
              signalen eerder herkent - voordat gedrag escaleert. En je leert
              hoe je je eigen lichaamstaal bewust inzet, zodat jouw boodschap
              ook daadwerkelijk aankomt.
            </p>

            <p className="text-blue-800 leading-relaxed text-lg font-semibold mb-4" data-id="element-605">
              Meer dan 100.000 Nederlandse en Vlaamse hondeneigenaren volgden
              het webinar al. Veel van hen zeiden achteraf hetzelfde: het was
              alsof er eindelijk ondertiteling werd aangezet.
            </p>

            <p className="text-gray-800 leading-relaxed text-lg mb-6" data-id="element-606">
              Meld je gratis aan. Eén webinar van 90 minuten kan veranderen hoe
              jij en je hond de rest van jullie leven met elkaar communiceren.
            </p>

            <button onClick={openPopup} className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-white font-bold py-4 px-8 rounded-lg text-lg transition-colors duration-200 shadow-lg" data-id="element-607">
              Bekijk webinar van Amee →
            </button>
          </div>

          <div className="w-full mt-6 mb-6" data-id="element-608">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/mPezTbx7aSGaxHbt8eLB8F/Scherm%C2%ADafbeelding_2025-08-15_om_15.37.25.png" alt="Vrouw in oranje trui communiceert met haar border collie in een park" className="w-full h-auto rounded-lg" data-id="element-609" />
            <p className="text-xs text-gray-500 mt-2 text-center italic" data-id="element-610">
              "In 90 minuten kan ik mensen de basisgrammatica van de hondentaal
              leren," stelt Amee
            </p>
          </div>
        </section>

        {/* AL HET ANDERE (Blijft exact hetzelfde) */}
        <ParticipantTestimonials data-id="element-611" />

        {/* Wat is een webinar? (inline, vervangt Science component) */}
        <section className="mb-12" data-id="element-612">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-613">
            Wat is een webinar?
          </h2>
          <p className="text-gray-800 leading-relaxed" data-id="element-614">
            Een webinar is een online presentatie die je live kunt volgen vanaf
            je telefoon, tablet of computer. Je hoeft nergens naartoe te reizen
            - je volgt gewoon thuis, op kantoor of onderweg via een
            internetverbinding. Tijdens het webinar kun je via de chat vragen
            stellen aan Amee, en na afloop ontvang je automatisch de opname
            zodat je alles terug kunt kijken wanneer het jou uitkomt.
          </p>
        </section>

        <ExpertProfile data-id="element-615" />
        <WebinarTimeline data-id="element-616" />
        <WebinarDetailsCard data-id="element-617" />
        <BonusSection data-id="element-618" />
        <TrustpilotReviews data-id="element-619" />
        <WhyDifferent data-id="element-620" />
        <FAQSections data-id="element-621" />
        <TransformationSummary data-id="element-622" />
        <FinalCTA data-id="element-623" />
        <ChatTestimonials data-id="element-624" />
        <NewFooter data-id="element-625" />
      </div>
      <StickyBar data-id="element-626" />
    </>;
};
export const Adv2bPage = () => {
  return <RegistrationProvider data-id="element-627">
      <Adv2bContent data-id="element-628" />
    </RegistrationProvider>;
};